import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { CreditCard, Check, AlertCircle, Shield, Users, Building2 } from 'lucide-react';
import api from '@/lib/api';
import { formatDateFromIso } from '@/lib/datetime';

export default function OwnerSubscription() {
    const [searchParams] = useSearchParams();
    const statusParam = searchParams.get('status');

    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [plans, setPlans] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [subscribing, setSubscribing] = useState(null);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    useEffect(() => {
        if (statusParam === 'success') {
            setSuccessMsg('¡Gracias! Tu pago fue procesado. La suscripción se activará en unos momentos.');
        } else if (statusParam === 'failure') {
            setError('El pago no se completó. Intentá de nuevo o contactá soporte.');
        } else if (statusParam === 'pending') {
            setSuccessMsg('Tu pago está pendiente. Se activará cuando se acredite.');
        }
    }, [statusParam]);

    const fetchData = async () => {
        setLoading(true);
        try {
                const [subRes, plansRes] = await Promise.all([
                    api.get('/owner/current-subscription'),
                    api.get('/owner/plans'),
                ]);
            setCurrentSubscription(subRes.data.data.subscription);
            setPayments(subRes.data.data.payments || []);
            setPlans((plansRes.data.data || []).filter(p => p.active && !p.is_lifetime_free));
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSubscribe = async (plan) => {
        if (isActive && !canUpgradeTo(plan)) return;

        // Confirm upgrade when already on an active plan
        if (isActive) {
            const fromName = currentSubscription.plan?.name || 'Plan actual';
            const fromLevel = currentLevel > 0 ? `(nivel ${currentLevel})` : '';
            const toLevel = (plan.level ?? 0) > 0 ? `(nivel ${plan.level})` : '';
            const confirmed = window.confirm(
                `¿Cambiar de "${fromName}" ${fromLevel} a "${plan.name}" ${toLevel}?\n\nTu suscripción actual se cancelará automáticamente y se activará el nuevo plan.`
            );
            if (!confirmed) return;
        }

        setSubscribing(plan.id);
        setError('');
        try {
            const res = await api.post('/owner/subscribe', { plan_id: plan.id });

            // Free plan activated immediately
            if (res.data.data?.auto_activated) {
                setSuccessMsg(res.data.message || 'Plan activado exitosamente.');
                await fetchData();
                return;
            }

            const { init_point, sandbox_init_point } = res.data.data;
            const url = init_point || sandbox_init_point;
            if (url) {
                window.location.href = url;
            } else {
                setError('No se pudo generar el link de pago.');
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Error al iniciar el pago.';
            setError(msg);
        } finally {
            setSubscribing(null);
        }
    };

    const isExpired = currentSubscription?.ends_at && new Date(currentSubscription.ends_at) < new Date();
    const isActive = currentSubscription?.status === 'active' && !isExpired;
    const isTrial = currentSubscription?.plan?.trial_duration_days !== null && currentSubscription?.plan?.trial_duration_days !== undefined;
    const currentLevel = currentSubscription?.plan?.level ?? 0;
    const daysRemaining = isActive && currentSubscription?.ends_at
        ? Math.max(0, Math.ceil((new Date(currentSubscription.ends_at) - new Date()) / (1000 * 60 * 60 * 24)))
        : 0;

    const canUpgradeTo = (plan) => {
        if (!isActive) return true;
        // Same plan: can't "upgrade" to itself
        if (currentSubscription?.plan?.id === plan.id) return false;
        // Trial is temporary — always allow upgrading to any other plan
        if (isTrial) return true;
        // Level-based restriction for paid plans
        const targetLevel = plan.level ?? 0;
        const curLevel = currentLevel;
        if (targetLevel > 0 && curLevel > 0) {
            return targetLevel > curLevel;
        }
        // Fallback: if levels aren't properly set, allow the upgrade
        return true;
    };

    const planFeatures = (plan) => {
        const feats = [];
        feats.push({ icon: Building2, text: `Hasta ${plan.max_gyms} gimnasio${plan.max_gyms > 1 ? 's' : ''}` });
        feats.push({ icon: Users, text: `Hasta ${plan.max_customers_per_gym} alumnos por gym` });
        if (plan.features) {
            if (Array.isArray(plan.features)) {
                plan.features.forEach(f => feats.push({ icon: Check, text: f }));
            } else if (typeof plan.features === 'object') {
                Object.entries(plan.features).forEach(([k, v]) => {
                    if (k === 'max_customers_per_gym' || k === 'max_gyms') return;
                    const label = k === 'support' ? 'Soporte' : k;
                    feats.push({ icon: Check, text: `${label}: ${v}` });
                });
            }
        }
        return feats;
    };

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Suscripciones</h1>

            {successMsg && (
                <div className="bg-white/5 text-white text-sm p-4 rounded-md flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 shrink-0" />
                    {successMsg}
                </div>
            )}
            {error && (
                <div className="bg-white/5 text-white text-sm p-4 rounded-md flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    {error}
                </div>
            )}

            {/* Current subscription */}
            <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                <CardHeader className="flex flex-row items-center gap-3 pb-2">
                    <Shield className="h-5 w-5 text-white" />
                    <CardTitle className="text-lg text-white uppercase tracking-wide">Suscripción actual</CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-white/60 text-center py-4">Cargando...</p>
                    ) : currentSubscription && !isActive ? (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="font-medium text-lg text-white">{currentSubscription.plan?.name || 'Plan'}</span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white/10 text-white">
                                    {isTrial ? 'Prueba expirada' : 'Expirada'}
                                </span>
                            </div>
                            <div className="text-sm text-white/60">
                                <p>Venció el: {formatDateFromIso(currentSubscription.ends_at)}</p>
                                {isTrial && (
                                    <p className="mt-2 text-white/80">Tu periodo de prueba terminó. Elegí un plan para seguir usando GymCard.</p>
                                )}
                            </div>
                        </div>
                    ) : isActive ? (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="font-medium text-lg text-white">{currentSubscription.plan?.name || 'Plan activo'}</span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white text-black">
                                    {isTrial ? 'Prueba activa' : 'Activa'}
                                </span>
                            </div>
                            {currentSubscription.is_lifetime ? (
                                <p className="text-sm text-white font-medium">Cuenta de por vida</p>
                            ) : (
                                <div className="text-sm text-white/60">
                                    <p>Válida hasta: {formatDateFromIso(currentSubscription.ends_at)}</p>
                                    {isTrial && daysRemaining > 0 && (
                                        <p className="mt-1 text-white/80">
                                            {daysRemaining} {daysRemaining === 1 ? 'día restante' : 'días restantes'} de prueba
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-sm text-white/60 py-4">
                            <AlertCircle className="h-4 w-4 text-white" />
                            No tenés una suscripción activa. Elegí un plan de abajo para empezar.
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Available plans */}
            <div>
                <h2 className="text-lg font-semibold mb-4 text-white uppercase tracking-wide">Planes disponibles</h2>
                {isActive && (
                    <div className="bg-white/5 text-white/80 text-sm p-3 rounded-md mb-4 flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                        Al elegir un nuevo plan, tu suscripción actual se cancelará automáticamente y se activará el nuevo plan inmediatamente después del pago.
                    </div>
                )}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {loading ? (
                        <p className="text-white/60">Cargando planes...</p>
                    ) : (
                        plans.map(plan => (
                            <Card key={plan.id} className={`bg-[#0a0a0a] border-[#3a3a3f] ${plan.slug === 'pro' ? 'border-white' : ''}`}>
                                <CardHeader className="text-center pb-2">
                                    <CardTitle className="text-xl text-white uppercase tracking-wide">{plan.name}</CardTitle>
                                    <p className="text-3xl font-bold mt-2 text-white">
                                        ${Number(plan.price).toLocaleString('es-AR')}
                                        <span className="text-sm font-normal text-white/60">/mes</span>
                                    </p>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <Separator className="bg-[#3a3a3f]" />
                                    <ul className="space-y-2">
                                        {planFeatures(plan).map((feat, idx) => (
                                            <li key={idx} className="flex items-center gap-2 text-sm text-white/60">
                                                <feat.icon className="h-4 w-4 text-white shrink-0" />
                                                {feat.text}
                                            </li>
                                        ))}
                                    </ul>
                                    <Button
                                        className="w-full"
                                        disabled={
                                            subscribing === plan.id ||
                                            (isActive && !canUpgradeTo(plan))
                                        }
                                        onClick={() => handleSubscribe(plan)}
                                        variant={plan.slug === 'pro' ? 'default' : 'outline'}
                                    >
                                        {subscribing === plan.id
                                            ? 'Cargando...'
                                            : isActive && currentSubscription?.plan?.id === plan.id
                                                ? 'Plan actual'
                                                : isActive && !canUpgradeTo(plan)
                                                    ? 'Plan inferior'
                                                    : plan.price == 0 && !plan.is_lifetime_free
                                                        ? 'Activar prueba'
                                                        : isActive
                                                            ? 'Actualizar plan'
                                                            : 'Suscribirme'}
                                    </Button>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>
            </div>

            {/* Payment history */}
            {payments.length > 0 && (
                <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                    <CardHeader className="flex flex-row items-center gap-3 pb-2">
                        <CreditCard className="h-5 w-5 text-white" />
                        <CardTitle className="text-lg text-white uppercase tracking-wide">Historial de pagos</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm min-w-[400px]">
                                <thead>
                                    <tr className="border-b border-[#3a3a3f]">
                                        <th className="text-left py-2 px-2 font-medium text-white/60 uppercase tracking-wider">Referencia</th>
                                        <th className="text-left py-2 px-2 font-medium text-white/60 uppercase tracking-wider">Monto</th>
                                        <th className="text-left py-2 px-2 font-medium text-white/60 uppercase tracking-wider">Estado</th>
                                        <th className="text-left py-2 px-2 font-medium text-white/60 uppercase tracking-wider">Fecha</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payments.map(p => (
                                        <tr key={p.id} className="border-b border-[#3a3a3f] last:border-0 hover:bg-white/5">
                                            <td className="py-2 px-2 font-mono text-xs text-white/60">{p.reference || '-'}</td>
                                            <td className="py-2 px-2 text-white">${Number(p.amount).toLocaleString('es-AR')}</td>
                                            <td className="py-2 px-2">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${p.status === 'completed' ? 'bg-white text-black' : 'bg-white/10 text-white'}`}>
                                                    {p.status}
                                                </span>
                                            </td>
                                            <td className="py-2 px-2 text-white/60">{formatDateFromIso(p.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
